import { AccordionItem, AccordionTrigger, AccordionContent } from '@/common/ui/accordion'

import { LabelInput } from '@/common/ui/LabelInput'

import Money from '@/common/models/money'

import useBundleTrade from './useBundleTrade'
import { getMinShares } from '@/trading/utils'
import { isLimitPriceValid, isSizeAboveMin, getStepSize } from '@/common/utils/utils'
import PrecisionAwarePriceLabel from '@/common/models/precsionAwarePriceLabel'
import type { HoldingT } from '@/common/types'
import type { SecurityOrderItem } from './types'
import { FC, useEffect, useState } from 'react'
import { ORDER_FORM_COPY } from '@/locales/en'
import { SecuritySummaryItem } from './SecuritySummaryItem'
import { analytics } from '@/common/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'

interface SecurityOrderAccordionItemProps {
    line: SecurityOrderItem
    totals: { totalAmount: number }
    currentHolding: HoldingT | undefined
}

export const SecurityOrderAccordionItem: FC<SecurityOrderAccordionItemProps> = ({ line, totals, currentHolding }) => {
    const { updateOrderItem, orderType, orderItems: lines, setTotalInvestmentAmount, bundleId } = useBundleTrade()
    const [sharesCurrentlyHeld, setSharesCurrentlyHeld] = useState<number | undefined>(undefined)
    const percent = totals.totalAmount > 0 ? ((line.amount ?? 0) / totals.totalAmount) * 100 : 0

    const getPrice = () => {
        if (orderType === 'LIMIT')
            return (
                line.relevantPrice ?? line.instrument.priceInfo.lastPrice ?? line.instrument.priceInfo.closingPrice ?? 0
            )
        return line.instrument.priceInfo.lastPrice ?? line.instrument.priceInfo.closingPrice ?? 0
    }

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = parseFloat(e.target.value)

        if (isNaN(value)) {
            value = 0
        }
        if (value < 0) return

        const amount = Money.of(value).valueWithPrecision(2)
        const price = getPrice()
        const units = price > 0 ? Math.floor(amount / price) : 0
        updateOrderItem(line.securityCode, { amount, units })
        const newTotal = lines.reduce((sum, l) => {
            if (l.securityCode === line.securityCode) {
                return sum + amount
            }
            return sum + l.amount
        }, 0)
        analytics.trackWithDefaultsDebounced(
            ANALYTICS_EVENTS.TRADING.BUNDLE_SECURITIES_AMOUNT_CHANGED,
            ANALYTICS_EVENTS.TRADING.BUNDLE_SECURITIES_AMOUNT_CHANGED,
            {
                bundle_id: bundleId,
                security_code: line.securityCode,
                amount,
                units,
            },
        )
        setTotalInvestmentAmount(newTotal)
    }

    const handleUnitsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = parseInt(e.target.value)

        if (isNaN(value)) {
            value = 0
        }
        if (value < 0) return

        const price = getPrice()
        const amount = Money.of(value * price).valueWithPrecision(2)
        updateOrderItem(line.securityCode, { units: value, amount })
        const newTotal = lines.reduce((sum, l) => {
            if (l.securityCode === line.securityCode) {
                return sum + amount
            }
            return sum + l.amount
        }, 0)
        analytics.trackWithDefaultsDebounced(
            ANALYTICS_EVENTS.TRADING.BUNDLE_SECURITIES_UNITS_CHANGED,
            ANALYTICS_EVENTS.TRADING.BUNDLE_SECURITIES_UNITS_CHANGED,
            {
                bundle_id: bundleId,
                security_code: line.securityCode,
                amount,
                units: value,
            },
        )
        setTotalInvestmentAmount(newTotal)
    }

    const handleLimitPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value)
        if (isNaN(value)) return
        if (value < 0) return

        const relevantPrice = Money.of(value).valueWithPrecision(2)
        const amount = Money.of((line.units ?? 0) * relevantPrice).valueWithPrecision(2)
        updateOrderItem(line.securityCode, { relevantPrice, amount })
        const newTotal = lines.reduce((sum, l) => {
            if (l.securityCode === line.securityCode) {
                return sum + amount
            }
            return sum + l.amount
        }, 0)
        analytics.trackWithDefaultsDebounced(
            ANALYTICS_EVENTS.TRADING.BUNDLE_SECURITIES_LIMIT_PRICE_CHANGED,
            ANALYTICS_EVENTS.TRADING.BUNDLE_SECURITIES_LIMIT_PRICE_CHANGED,
            {
                bundle_id: bundleId,
                security_code: line.securityCode,
                amount,
                limit_price: relevantPrice,
            },
        )
        setTotalInvestmentAmount(newTotal)
    }

    const handleMinimumTradeSizeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault()
        const price = getPrice()
        const min = getMinShares('BUY', sharesCurrentlyHeld ?? 0, price)
        const amount = Money.of(min * price).valueWithPrecision(2)
        updateOrderItem(line.securityCode, { units: min, amount })
        const newTotal = lines.reduce((sum, l) => {
            if (l.securityCode === line.securityCode) {
                return sum + amount
            }
            return sum + l.amount
        }, 0)
        analytics.trackWithDefaultsDebounced(
            ANALYTICS_EVENTS.TRADING.BUNDLE_SECURITIES_MINIMUM_CLICKED,
            ANALYTICS_EVENTS.TRADING.BUNDLE_SECURITIES_MINIMUM_CLICKED,
            {
                bundle_id: bundleId,
                security_code: line.securityCode,
                amount,
                units: min,
            },
        )
        setTotalInvestmentAmount(newTotal)
    }

    useEffect(() => {
        if (currentHolding) {
            setSharesCurrentlyHeld(currentHolding.units)
        }
    }, [currentHolding, line.securityCode])

    // Error state detection
    const isLimitPriceError =
        orderType === 'LIMIT' &&
        !!line.relevantPrice &&
        !isLimitPriceValid(
            line.relevantPrice,
            line.instrument.priceInfo.lastPrice ?? line.instrument.priceInfo.closingPrice ?? 0,
        )
    const isBelowMinError =
        line.units > 0 &&
        !isSizeAboveMin(
            sharesCurrentlyHeld,
            line.relevantPrice ?? line.instrument.priceInfo.lastPrice ?? line.instrument.priceInfo.closingPrice ?? 0,
            line.units ?? 0,
        )

    const hasError = isLimitPriceError || isBelowMinError

    useEffect(() => {
        if (line.hasLimitPriceError !== isLimitPriceError || line.hasAmountError !== isBelowMinError) {
            updateOrderItem(line.securityCode, {
                hasLimitPriceError: isLimitPriceError,
                hasAmountError: isBelowMinError,
            })
        }
    }, [
        isLimitPriceError,
        isBelowMinError,
        line.hasLimitPriceError,
        line.hasAmountError,
        line.securityCode,
        updateOrderItem,
    ])
    return (
        <AccordionItem
            value={line.securityCode}
            className={`w-full rounded-lg ${hasError ? 'border border-sentiment-bad' : 'border-none'}`}
        >
            <AccordionTrigger className="self-stretch w-full p-2 bg-stone-50 rounded-lg inline-flex justify-between items-center hover:no-underline">
                <SecuritySummaryItem
                    securityCode={line.instrument.securityCode}
                    weight={percent}
                    amount={line.amount}
                    units={line.units}
                    relevantPrice={getPrice()}
                />
            </AccordionTrigger>
            <AccordionContent className="pb-0">
                <div className="self-stretch w-full text-xs flex flex-col justify-start items-start gap-[10px] px-2 py-2 text-content-secondary bg-stone-50 border-t border-stone-200 rounded-b-lg font-['Inter']">
                    {orderType === 'LIMIT' && (
                        <>
                            <LabelInput
                                type="number"
                                className="!pl-0 py-2 pr-3 text-right items-end text-black text-sm font-['Inter'] h-9"
                                labelClassName="text-sm"
                                isVertical={false}
                                value={line.relevantPrice ?? line.instrument.priceInfo.lastPrice}
                                prefixIcon={<div className="font-light text-[#6B7280]">$</div>}
                                iconClassName="max-w-[100px]"
                                onChange={handleLimitPriceChange}
                                label={'Price per unit'}
                                placeholder="0"
                                step=".01"
                                id="limit-price"
                            />
                            {isLimitPriceError ? (
                                <p className="text-xs self-start text-sentiment-bad">{`Price must be between ${PrecisionAwarePriceLabel.of((line.instrument.priceInfo.lastPrice ?? line.instrument.priceInfo.closingPrice ?? 0) * 0.95).toString()} and ${PrecisionAwarePriceLabel.of((line.instrument.priceInfo.lastPrice ?? line.instrument.priceInfo.closingPrice ?? 0) * 1.05).toString()} and multiple of ${getStepSize(line.relevantPrice!)}`}</p>
                            ) : null}
                        </>
                    )}
                    <LabelInput
                        type="number"
                        className="pr-3 py-2 text-right items-end max-w-[100px] text-black text-sm font-['Inter'] h-9"
                        labelClassName="text-sm"
                        isVertical={false}
                        value={line.units}
                        iconClassName="max-w-[100px]"
                        onChange={handleUnitsChange}
                        label={'Units'}
                        placeholder="0"
                        step="1"
                        id="units"
                    />
                    <LabelInput
                        type="number"
                        className="!pl-0 pr-3 text-right py-2 items-end text-black text-sm font-['Inter'] h-9"
                        labelClassName="text-sm"
                        isVertical={false}
                        value={line.amount}
                        prefixIcon={<div className="font-light text-[#6B7280]">$</div>}
                        iconClassName="max-w-[100px]"
                        onChange={handleAmountChange}
                        label={'Amount'}
                        placeholder="0"
                        step=".01"
                        id="unit-amount"
                    />
                    {isBelowMinError ? (
                        <p className="text-xs self-start text-sentiment-bad">
                            {ORDER_FORM_COPY.warnings.belowMinimum}
                            <a className="text-inherit underline cursor-pointer" onClick={handleMinimumTradeSizeClick}>
                                {ORDER_FORM_COPY.setMinimum}
                            </a>
                        </p>
                    ) : null}
                </div>
            </AccordionContent>
        </AccordionItem>
    )
}

export default SecurityOrderAccordionItem
